import { yupResolver } from '@hookform/resolvers/yup';
import { StyleDeclarationValue, css } from 'aphrodite';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FormFieldType } from '../../../../../../../extViewmodels';
import { useClientUpdateSatisfactionResponseMutation } from '../../../../../../../queries';
import { Button } from '../../../../../../../web/components/Button';
import { DeprecatedTextArea } from '../../../../../../../web/components/DeprecatedTextArea';
import { bs } from '../../../../../../../web/styles/styles';
import {
	CustomFormMetaData,
	IPublicFileAttachment,
	ISatisfactionSurvey,
	ISatisfactionSurveyResponse,
} from '../../../../../../models/Api';
import { buildCustomPropertiesSchema } from '../../../../../../utils/validation';
import { useErrorMessagesContext } from '../../../../../../components/ErrorModalProvider/context';
import { FileUpload } from '../../../../shared/FileUpload';
import {
	AdditionalSurveyQuestionCheckboxField,
	AdditionalSurveyQuestionDropdownField,
	AdditionalSurveyQuestionInputField,
} from './presentation';
import { styleSheet } from './styles';

interface IFormValues {
	feedback?: string;
	customProperties: Record<string, string | IPublicFileAttachment[]>;
}

interface IProps {
	readonly className?: string;
	readonly styles?: StyleDeclarationValue[];
	readonly survey: ISatisfactionSurvey;
	readonly response: ISatisfactionSurveyResponse;
	readonly setShowThankYouMessage?: React.Dispatch<React.SetStateAction<boolean>>;
}

export function AdditionalSurveyQuestions({
	className = '',
	styles = [],
	survey,
	response,
	setShowThankYouMessage,
}: IProps) {
	const satisfactionSurveyRef = React.useRef(survey).current;
	const surveyMutation = useClientUpdateSatisfactionResponseMutation({});
	const { mutateAsync: updateResponseAsync } = surveyMutation;
	const { pushError } = useErrorMessagesContext();

	// Filter questions based on custom form metadata and response.rating
	const questions = satisfactionSurveyRef?.customForm?.fields;
	const filteredQuestions = React.useMemo(() => {
		return (
			questions?.filter(field => {
				const customMetaData: CustomFormMetaData = satisfactionSurveyRef.customFormMetaData.find(
					(md: CustomFormMetaData) => md.fieldId === field.id
				);
				if (field.isHidden) return false;
				if (!customMetaData?.ratings) return true;
				return customMetaData.ratings.includes(response.rating);
			}) || []
		);
	}, [questions, satisfactionSurveyRef.customFormMetaData, response.rating]);

	// Build Yup validation schema for custom properties and feedback
	const validationSchema = React.useMemo((): yup.ObjectSchema<any> => {
		return yup.object().shape({
			feedback: yup.string(), // optional feedback
			customProperties: filteredQuestions?.length
				? buildCustomPropertiesSchema(
						filteredQuestions.map(field => ({
							...field,
							id: field.id || '',
						}))
					)
				: yup.object().shape({}),
		});
	}, [filteredQuestions]);

	// Build default values from the response
	const defaultValues: IFormValues = React.useMemo(() => {
		const defaultCustomProperties: Record<string, string | IPublicFileAttachment[]> = {};
		filteredQuestions?.forEach(q => {
			if (q.fieldType === FormFieldType.FileUpload) {
				defaultCustomProperties[q.id] = (response.customProperties?.[q.id] as IPublicFileAttachment[]) || [];
			} else {
				defaultCustomProperties[q.id] = (response.customProperties?.[q.id] as string) || '';
			}
		});
		return {
			feedback: response.feedback || '',
			customProperties: defaultCustomProperties,
		};
	}, [filteredQuestions, response]);

	const {
		handleSubmit,
		control,
		formState: { errors, isSubmitting, isSubmitted },
	} = useForm<IFormValues>({
		defaultValues,
		resolver: yupResolver(validationSchema),
		mode: 'onBlur', // or 'onChange'
	});

	// Submission using react-hook-form's handleSubmit.
	const onSubmit = async (data: IFormValues) => {
		try {
			await updateResponseAsync({
				accessToken: response.token,
				response: { ...response, customProperties: data.customProperties, feedback: data.feedback },
			});
			setShowThankYouMessage(true);
		} catch (error) {
			pushError(error);
		}
	};

	const onCancelClicked = () => {
		setShowThankYouMessage(true);
	};

	return (
		<div className={`${css(styleSheet.additionalQuestions, ...styles)} ${className}`}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className={css(styleSheet.body)}>
					<div className={css(styleSheet.questionsWrapper)}>
						{/* Feedback field */}
						{filteredQuestions?.length === 0 ? (
							<>
								<label className={css(styleSheet.feedbackLabel)} htmlFor='take-survey-feedback-input'>
									<span>Why did you give this rating?</span>&nbsp;
									<span>(Optional)</span>
								</label>
								<Controller
									name='feedback'
									control={control}
									render={({ field }) => (
										<>
											<DeprecatedTextArea
												inputClassName={css(styleSheet.feedbackInput)}
												inputId='take-survey-feedback-input'
												placeholder='Feedback'
												onChange={field.onChange}
												value={field.value}
											/>
											{isSubmitted && errors.feedback ? (
												<p className={css(styleSheet.errorMessage)}>{errors.feedback.message}</p>
											) : null}
										</>
									)}
								/>
							</>
						) : null}

						{/* Render each survey question with its own Controller */}
						{filteredQuestions?.map((fieldItem, i) => {
							const fieldName = `customProperties.${fieldItem.id}` as const;
							if (fieldItem.fieldType === FormFieldType.String) {
								return (
									<Controller
										key={fieldItem.id}
										name={fieldName}
										control={control}
										render={({ field }) => (
											<>
												<AdditionalSurveyQuestionInputField
													field={fieldItem}
													index={i}
													filteredQuestions={filteredQuestions}
													value={field.value as string}
													handleFieldChange={field.onChange}
													onBlur={field.onBlur}
												/>
												{errors.customProperties?.[fieldItem.id] && (
													<p className={css(styleSheet.errorMessage)}>
														{errors.customProperties[fieldItem.id]?.message}
													</p>
												)}
											</>
										)}
									/>
								);
							}
							if (fieldItem.fieldType === FormFieldType.Option) {
								return (
									<Controller
										key={fieldItem.id}
										name={fieldName}
										control={control}
										render={({ field }) => (
											<>
												<AdditionalSurveyQuestionDropdownField
													field={fieldItem}
													index={i}
													filteredQuestions={filteredQuestions}
													value={field.value as string}
													setValue={field.onChange}
												/>
												{errors.customProperties?.[fieldItem.id] && (
													<p className={css(styleSheet.errorMessage)}>
														{errors.customProperties[fieldItem.id]?.message}
													</p>
												)}
											</>
										)}
									/>
								);
							}
							if (fieldItem.fieldType === FormFieldType.MultipleOptions) {
								return (
									<Controller
										key={fieldItem.id}
										name={fieldName}
										control={control}
										render={({ field }) => (
											<>
												<AdditionalSurveyQuestionCheckboxField
													field={fieldItem}
													index={i}
													filteredQuestions={filteredQuestions}
													value={field.value as string}
													setValue={field.onChange}
												/>
												{errors.customProperties?.[fieldItem.id] && (
													<p className={css(styleSheet.errorMessage)}>
														{errors.customProperties[fieldItem.id]?.message}
													</p>
												)}
											</>
										)}
									/>
								);
							}
							if (fieldItem.fieldType === FormFieldType.FileUpload) {
								return (
									<Controller
										key={fieldItem.id}
										name={fieldName}
										control={control}
										render={({ field: { value, onChange }, fieldState: { error } }) => (
											<div className={css(styleSheet.inputWrap)}>
												<div className={css(styleSheet.inputWrapHeader)}>
													<label className={css(bs.block, bs.textLeft)} htmlFor={fieldItem.id}>
														{fieldItem.label}{' '}
														{!fieldItem.isOptional ? <sup className={css(styleSheet.errorMessage)}>*</sup> : null}
													</label>
													<small className={css(styleSheet.smallText, bs.shrink0)}>
														{`${i + 1} of ${filteredQuestions.length}`}
													</small>
												</div>
												<FileUpload
													token={response.token ?? ''}
													surveyType='satisfaction'
													onFileSelected={onChange}
													files={(value as IPublicFileAttachment[]) ?? []}
													error={error}
												/>
											</div>
										)}
									/>
								);
							}
							return null;
						})}
					</div>
					<footer className={css(styleSheet.footer)}>
						<Button
							className={css(bs.ctaButton)}
							kind='primary'
							label='Submit Feedback'
							disabled={isSubmitting}
							isLoading={isSubmitting}
							type='submit'
						/>
						<Button className={css(styleSheet.cancel)} kind='custom' label='Close Survey' onClick={onCancelClicked} />
					</footer>
				</div>
			</form>
		</div>
	);
}
